import type { Locale } from '@/i18n/config'

// Pure view helpers — date/id formatting and clipboard. No envelope peeling.
// Transport adapter (src/lib/transport.ts + src/lib/api.ts) is the sole owner of envelope peeling.

// ID display convention: short code + full tooltip + copy.
// Spec: "有的全量 36 字符 break-all、有的截 8 位、有的截 12 位" → unify to short 8 with middle-ellipsis helper.
// shortId: first N chars (default 8) — for dense lists, matches previous safeSlice(8) behavior but handles null/undefined.
// shortIdMiddle: head…tail (default 4…4) — preserves uniqueness on ULID overlap, for detail views.

export function shortId(value: unknown, len = 8): string {
  const s = String(value ?? '')
  if (!s) return '—'
  if (s.length <= len) return s
  return s.slice(0, len)
}

export function shortIdMiddle(value: unknown, head = 4, tail = 4): string {
  const s = String(value ?? '')
  if (!s) return '—'
  if (s.length <= head + tail + 1) return s
  return `${s.slice(0, head)}…${s.slice(-tail)}`
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through
  }
  // non-secure context fallback
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

// Relative time — "Xm ago" helpers for secondary line (ID + relative time split).
// formatRelativeTimeLocale buckets the gap, Intl phrases the unit, and the
// absolute timestamp rides along as a locale-formatted title.
function parseDateValue(value: unknown): Date | null {
  if (value == null || value === '') return null
  const d = new Date(String(value))
  return Number.isNaN(d.getTime()) ? null : d
}

function localeTag(locale: Locale): string {
  return locale === 'zh' ? 'zh-CN' : 'en-US'
}

export function formatDateLocale(value: unknown, locale: Locale): string {
  const d = parseDateValue(value)
  if (d === null) return value == null || value === '' ? '—' : String(value)
  try {
    // 用户本地时区 — 不再把绝对时间强制转成 UTC 裸展示；绝对值仅保留在 title 里由调用方决定
    return new Intl.DateTimeFormat(localeTag(locale), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d)
  } catch {
    return d.toISOString().slice(0, 16).replace('T', ' ')
  }
}

// CLDR phrases the unit — `narrow` + `always` is the compact "5m ago" / "5分钟前"
// the console ships. Built once per locale: an Intl formatter is not free to
// construct per call, and these run once per list row.
const RELATIVE_FORMATTERS: Record<Locale, Intl.RelativeTimeFormat> = {
  zh: new Intl.RelativeTimeFormat(localeTag('zh'), { numeric: 'always', style: 'narrow' }),
  en: new Intl.RelativeTimeFormat(localeTag('en'), { numeric: 'always', style: 'narrow' }),
}

export function formatRelativeTimeLocale(value: unknown, locale: Locale): { label: string; title: string } {
  if (value == null || value === '') return { label: '—', title: '' }
  const d = parseDateValue(value)
  if (!d) return { label: String(value), title: String(value) }
  const title = formatDateLocale(value, locale)
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000))
  // Sub-minute is ours alone — no Intl unit says "just now".
  if (diffSec < 60) return { label: locale === 'zh' ? '刚刚' : 'just now', title }
  const rtf = RELATIVE_FORMATTERS[locale]
  if (diffSec < 3600) return { label: rtf.format(-Math.floor(diffSec / 60), 'minute'), title }
  if (diffSec < 86400) return { label: rtf.format(-Math.floor(diffSec / 3600), 'hour'), title }
  if (diffSec < 30 * 86400) return { label: rtf.format(-Math.floor(diffSec / 86400), 'day'), title }
  return { label: title, title }
}


